// pms-adapters / supabase / repositories — the driven-port implementations.
//
// Each class implements a pms-core repository port by calling the P1 RPCs /
// tables. Writes DELEGATE to the atomic RPCs — no invariant is re-implemented
// here (PMS-P2-ARCHITECTURE-BRIEF §2/§3). Reads map rows via ./rows.
//
// Methods annotated (P2) target RPCs from the §8 schema draft that are not yet
// migrated; they are unused by the P1 UI and go live when that migration lands.

import {
  err,
  ok,
  type AvailabilityCell,
  type AvailabilityQuery,
  type BedConfig,
  type BedType,
  type BillingMode,
  type Cents,
  type ChannexOccupancyOption,
  type ChannexRatePlanLink,
  type Charge,
  type CancellationFeeLine,
  type CancellationReason,
  type StayTermsBase,
  type TrainingResetReport,
  type ChargeDay,
  type ChargeId,
  type Folio,
  type FolioId,
  type OccupancySpan,
  type StayPerson,
  type StayPersonMeal,
  type StayPersonNeeds,
  type OccupancySpanInput,
  type OtaService,
  type Product,
  type ProductDraft,
  type ProductId,
  type RoomConfigPatch,
  type NewRoom,
  type CreateReservationInput,
  type CustomerSubtype,
  type Guest,
  type GuestDraft,
  type GuestId,
  type ReservationCommand,
  type QueryScope,
  type GuestRepository,
  type GuestSearch,
  type GuestSearchResult,
  type GuestSheet,
  type GuestSheetPatch,
  type InboundApplied,
  type InboundBooking,
  type IsoDate,
  type PmsPropertyMode,
  type Property,
  type PropertyRepository,
  type Rate,
  type RateRepository,
  type RateParameterSet,
  type StoredRateParameterSet,
  type RatePlanId,
  type ScopeSource,
  type ReservationEvent,
  type ReservationId,
  type ReservationPatch,
  type ReservationRepository,
  type ReservationView,
  type Reservation,
  type RackWindow,
  type BookingSearchResult,
  type Result,
  type Room,
  type BedSpace,
  type RoomBed,
  type RoomBlock,
  type RoomId,
  type RoomStayId,
  type RoomStayInput,
  type RoomStayView,
  type RoomRepository,
  type RoomType,
  type RoomTypeId,
  type AssignRoomStayOptions,
} from '@lemontaigu/pms-core';
import {
  rowToAvailabilityCell,
  rowToChannexRatePlanLink,
  rowToCharge,
  rowToChargeDay,
  rowToFolio,
  rowToOccupancySpan,
  rowToStayPerson,
  rowToStayPersonMeal,
  rowToStayPersonNeeds,
  rowToOtaService,
  rowToGuest,
  rowToGuestSummary,
  rowToGuestSheet,
  rowToCustomerSubtype,
  rowToProduct,
  rowToRate,
  rateRestrictionFields,
  rateRestrictionColumns,
  type RateRow,
  rowToRatePlan,
  rowToRateParameterSet,
  rowToReservationEvent,
  rowToReservationView,
  rowToBedConfig,
  rowToBedType,
  rowToRoom,
  rowToRoomBed,
  rowToRoomBlock,
  rowToRoomStay,
  rowToRoomType,
  type AvailabilityRow,
  type BedTypeRow,
  type RoomConfigOptionRow,
  type ChargeDayRow,
  type ChannexRatePlanRow,
  type ChargeRow,
  type FolioRow,
  type OccupancyRow,
  type StayPersonRow,
  type StayPersonMealRow,
  type StayPersonNeedsRow,
  type OtaServiceRow,
  type GuestRow,
  type GuestSearchRow,
  type GuestSheetRow,
  type CustomerSubtypeRow,
  type ProductRow,
  type RatePlanRow,
  type RateParameterSetRow,
  type ReservationEventRow,
  type ReservationRow,
  type RoomBedRow,
  type RoomBlockRow,
  type BedConfigRow,
  type RoomRow,
  type RoomTypeRow,
  type RoomStayRow,
  toBookingSearch,
  type BookingSearchJson,
} from '../rows.js';
import {
  fromList,
  fromResult,
  fromVoid,
  scopedInsert,
  scopedQuery,
  scopedRpcProperty,
  toPmsError,
  type SupabaseClient,
} from './support.js';
import type { PostgrestError } from '@supabase/supabase-js';

const RESERVATION_SELECT = '*, pms_guest(*), pms_room(room_number)';
const ROOM_STAY_SELECT = '*, pms_room(room_number)';

/**
 * Domain → the jsonb `pms_reservation_apply` reads (A1). Every key is written
 * out rather than spread from the domain shape: the RPC extracts keys by name,
 * so a mismatch here would be a silently ignored field.
 *
 * `booking` keeps ONLY the keys the command carries — in a patch an absent key
 * keeps the stored value and a null clears it, so `undefined` must not become
 * `null` on the wire. JSON.stringify drops undefined, and the spread below is
 * explicit about it rather than relying on that.
 */
function commandPayload(command: ReservationCommand, scope: QueryScope): Record<string, unknown> {
  const { envelope, guest, booking, rooms } = command;
  return {
    envelope: {
      source: envelope.source,
      mode: envelope.mode,
      reservationId: envelope.reservationId,
      externalRef: envelope.externalRef,
      revision: envelope.revision,
      propertyCode: scopedRpcProperty(scope),
    },
    guest: {
      id: guest.id,
      lastName: guest.draft.lastName,
      firstName: guest.draft.firstName,
      email: guest.draft.email,
      phone: guest.draft.phone,
      lang: guest.draft.lang,
      notes: guest.draft.notes,
    },
    booking: {
      ...(booking.status === undefined ? {} : { status: booking.status }),
      ...(booking.channel === undefined ? {} : { channel: booking.channel }),
      ...(booking.channelRef === undefined ? {} : { channelRef: booking.channelRef }),
      ...(booking.notes === undefined ? {} : { notes: booking.notes }),
      // PB4 — absent keeps (patch) or defaults (create); never sent as undefined.
      ...(booking.guarantee === undefined ? {} : { guarantee: booking.guarantee }),
      ...(booking.vadAmountCents === undefined ? {} : { vadAmountCents: booking.vadAmountCents }),
      // PB10 — the day an arrhes transfer is due; only with `transfer_pending`.
      ...(booking.transferDueOn === undefined ? {} : { transferDueOn: booking.transferDueOn }),
      ...(booking.arrivalFrom === undefined ? {} : { arrivalFrom: booking.arrivalFrom }),
      ...(booking.arrivalTo === undefined ? {} : { arrivalTo: booking.arrivalTo }),
    },
    rooms: rooms.map((r) => ({
      ...roomPayload(r),
      products: r.products.map((x) => ({ productId: x.productId, quantity: x.quantity })),
    })),
  };
}

/**
 * Domain beds -> the jsonb array `pms_apply_room_beds` reads. `roomId` is NOT
 * sent: the RPC already knows which unit it is writing, and sending it would
 * create a second, disagreeable source for the same fact.
 *
 * null and undefined both mean « not managing spaces » and must stay distinct
 * from `[]`, which means « this unit has none » and DELETES them.
 */
function bedsPayload(beds: readonly BedSpace[] | null | undefined): unknown[] | null {
  if (beds === null || beds === undefined) return null;
  return beds.map((b) => ({
    spaceKind: b.spaceKind,
    spaceIndex: b.spaceIndex,
    bedConfig: b.bedConfig,
    quantity: b.quantity,
  }));
}

/** One logement → the object `pms_write_room_stays` reads. The keys are ITS
 *  keys (camelCase inside the jsonb, unlike the p_-prefixed RPC arguments) —
 *  a mismatch here is a silently ignored field, so they are written out rather
 *  than spread from the domain shape. */
function roomPayload(r: RoomStayInput): Record<string, unknown> {
  return {
    id: r.id ?? null,
    roomTypeId: r.roomTypeId,
    ratePlanId: r.ratePlanId,
    roomId: r.roomId ?? null,
    checkin: r.checkin,
    checkout: r.checkout,
    adults: r.adults,
    children: r.children,
    infants: r.infants ?? 0,
    occupantName: r.occupantName ?? null,
    priceCents: r.priceCents,
    nights: r.nights ?? null,
    products: r.products ?? null,
    // ABSENT, not null, when not sent: an absent key keeps the stored persons
    // and switches (the patch rule of group edit step 3); null would be read
    // as a value.
    ...(r.persons === undefined
      ? {}
      : {
          persons: r.persons.map((p) => ({
            id: p.id ?? null,
            name: p.name,
            ageBand: p.ageBand,
            startDate: p.startDate,
            endDate: p.endDate,
            ...(p.meals === undefined
              ? {}
              : { meals: p.meals.map((m) => ({ serviceDate: m.serviceDate, prestation: m.prestation, taken: m.taken })) }),
            // Health data (step 5): absent unless the desk may see it — the
            // database refuses either key from anyone without pms.needs.read.
            ...(p.needs === undefined ? {} : { needs: p.needs }),
            ...(p.allergens === undefined ? {} : { allergens: [...p.allergens] }),
          })),
        }),
    ...(r.meals === undefined ? {} : { meals: [...r.meals] }),
    // Same rule: absent keeps the stored sold category (the Vega seed's too).
    ...(r.soldRoomTypeId === undefined ? {} : { soldRoomTypeId: r.soldRoomTypeId }),
    // PB4: the desk's terms for this guest. Absent keeps the plan's / the stored ones.
    ...(r.cancellationTerms === undefined
      ? {}
      : {
          cancellationTerms: {
            prepaymentPct: r.cancellationTerms.prepaymentPct,
            prepaymentAt: r.cancellationTerms.prepaymentAt,
            tiers: r.cancellationTerms.tiers.map((t) => ({ moreThanDaysBefore: t.moreThanDaysBefore, retainPct: t.retainPct })),
            noShowPct: r.cancellationTerms.noShowPct,
            // PB10: the policy the desk picked — the database takes its code and label from it.
            ...(r.cancellationTerms.policyCode === undefined ? {} : { policyCode: r.cancellationTerms.policyCode }),
          },
        }),
    // The bed configuration asked for. Absent keeps the stored one (the Vega seed's too).
    ...(r.requestedBedConfig === undefined ? {} : { requestedBedConfig: r.requestedBedConfig }),
  };
}

export class SupabaseReservationRepository implements ReservationRepository {
  constructor(
    private readonly db: SupabaseClient,
    private readonly scope: ScopeSource,
  ) {}

  /** Which of these channel booking ids we already hold. One `in.(…)` query,
   *  so the tier-2 sweep can distinguish a recovery from a booking we have had
   *  all along — and alert only on the former. */
  async knownChannexBookingIds(ids: readonly string[]): Promise<Result<string[]>> {
    if (ids.length === 0) return ok([]);
    const r = fromList<{ channex_booking_id: string | null }, string>(
      await scopedQuery(
        this.db.from('pms_reservation').select('channex_booking_id').in('channex_booking_id', [...ids]),
        this.scope.current(),
      ),
      (row) => row.channex_booking_id ?? '',
    );
    return r.ok ? ok(r.value.filter((x) => x !== '')) : r;
  }

  async create(input: CreateReservationInput): Promise<Result<ReservationId>> {
    const r = await this.db.rpc('pms_create_reservation', {
      p_property_code: scopedRpcProperty(this.scope.current()),
      p_guest_id: input.guestId,
      p_room_type_id: input.roomTypeId,
      p_checkin: input.checkin,
      p_checkout: input.checkout,
      p_adults: input.adults,
      p_children: input.children,
      p_status: input.status,
      p_source: input.source,
      p_channel: input.channel,
      p_channel_ref: input.channelRef,
      p_rate_plan_id: input.ratePlanId,
      p_price_cents: input.priceCents,
      p_notes: input.notes,
      ...(input.nights === undefined ? {} : { p_nights: input.nights }),
      ...(input.products === undefined ? {} : { p_products: input.products }),
      ...(input.rooms === undefined ? {} : { p_rooms: input.rooms.map(roomPayload) }),
    });
    return fromResult<ReservationId>(r);
  }

  async update(id: ReservationId, patch: ReservationPatch): Promise<Result<ReservationId>> {
    const r = await this.db.rpc('pms_update_reservation', {
      p_id: id,
      p_guest_id: patch.guestId ?? null,
      p_room_type_id: patch.roomTypeId ?? null,
      p_checkin: patch.checkin,
      p_checkout: patch.checkout,
      p_adults: patch.adults ?? null,
      p_children: patch.children ?? null,
      p_channel: patch.channel ?? null,
      p_channel_ref: patch.channelRef ?? null,
      p_rate_plan_id: patch.ratePlanId,
      p_price_cents: patch.priceCents,
      p_notes: patch.notes ?? null,
      ...(patch.nights === undefined ? {} : { p_nights: patch.nights }),
      ...(patch.products === undefined ? {} : { p_products: patch.products }),
      ...(patch.rooms === undefined ? {} : { p_rooms: patch.rooms.map(roomPayload) }),
    });
    return fromResult<ReservationId>(r);
  }

  async apply(command: ReservationCommand): Promise<Result<ReservationId>> {
    return fromResult<ReservationId>(
      await this.db.rpc('pms_reservation_apply', { p_command: commandPayload(command, this.scope.current()) }),
    );
  }

  async assignRoom(id: ReservationId, roomId: RoomId | null): Promise<Result<void>> {
    return fromVoid(
      await this.db.rpc('pms_assign_room', { p_reservation_id: id, p_room_id: roomId }),
    );
  }

  async assignRoomStay(id: RoomStayId, roomId: RoomId | null, opts?: AssignRoomStayOptions): Promise<Result<void>> {
    return fromVoid(
      await this.db.rpc('pms_assign_room_stay', {
        p_room_stay_id: id,
        p_room_id: roomId,
        // Only sent when asked for, so a database without 20261029200100 still answers the plain call.
        ...(opts?.allowOtherCategory ? { p_allow_other_category: true } : {}),
      }),
    );
  }

  async transition(
    id: ReservationId,
    action: string,
    reason?: string | null,
    reasonCode?: string | null,
    fee?: readonly CancellationFeeLine[] | null,
  ): Promise<Result<Reservation['status']>> {
    return fromResult<Reservation['status']>(
      await this.db.rpc('pms_reservation_transition', {
        p_id: id,
        p_action: action,
        // OMITTED when there is no reason, and that is deliberate — the first
        // version of this sent `p_reason: null` always, which would have taken
        // the whole desk down for the length of a Cloudflare build.
        //
        // PostgREST resolves an RPC by the argument NAMES supplied. This change
        // cannot ship its migration separately (the mirror test compares repo
        // SQL to repo TypeScript, so both land in one commit), which means the
        // SPA can reach production before the migration is applied. Sending
        // three names to a database that still has the two-argument function
        // matches nothing, and EVERY transition fails — not just the new ones.
        // Sending two resolves against the old signature, and equally against
        // the new one, where p_reason defaults. So the omission is what makes
        // the deploy order stop mattering for every pre-existing action.
        ...(reason == null || reason === '' ? {} : { p_reason: reason }),
        // Same omission rule, same reason, one signature later (0138): naming
        // p_reason_code matches nothing on a database still holding the
        // three-argument function, so it is sent ONLY when there is a code to
        // send. Every pre-existing call keeps resolving either way.
        ...(reasonCode == null || reasonCode === '' ? {} : { p_reason_code: reasonCode }),
        // OB3 (20261101210100): sent only with a fee, for the same reason again.
        ...(fee == null || fee.length === 0 ? {} : { p_fee: fee }),
      }),
    );
  }

  async cancellationReasons(): Promise<Result<CancellationReason[]>> {
    const r = await this.db
      .from('pms_cancellation_reason')
      .select('code, label_fr, is_correction')
      .eq('active', true)
      .order('sort_order', { ascending: true });
    return fromList<{ code: string; label_fr: string; is_correction: boolean }, CancellationReason>(r, (x) => ({
      code: x.code,
      labelFr: x.label_fr,
      isCorrection: x.is_correction,
    }));
  }

  async currentDay(propertyCode: string): Promise<Result<IsoDate | null>> {
    const r = await this.db
      .from('pms_property_day')
      .select('current_day')
      .eq('property_code', propertyCode)
      .maybeSingle();
    if (r.error) return fromVoid(r) as Result<never>;
    return ok(((r.data as { current_day: string } | null)?.current_day ?? null) as IsoDate | null);
  }

  async closeDay(propertyCode: string, expectedDay: IsoDate): Promise<Result<IsoDate>> {
    const r = await this.db.rpc('pms_close_day', { p_property: propertyCode, p_expected_day: expectedDay });
    if (r.error) return fromVoid(r) as Result<never>;
    const v = (r.data ?? null) as { currentDay?: string } | null;
    if (!v?.currentDay) return err('unknown', 'Passage de jour : réponse illisible', { retryable: false });
    return ok(v.currentDay as IsoDate);
  }

  async resetTraining(propertyCode: string): Promise<Result<TrainingResetReport>> {
    const r = await this.db.rpc('pms_training_reset', { p_property_code: propertyCode });
    if (r.error) return readRpcError(r.error, 'Accès réservé — remise à zéro de la formation');
    const v = (r.data ?? null) as { reservations?: number; guests?: number; kept?: number; currentDay?: string } | null;
    if (v === null || typeof v.reservations !== 'number') {
      return err('unknown', 'Remise à zéro : réponse illisible', { retryable: false });
    }
    return ok({
      reservations: v.reservations,
      guests: v.guests ?? 0,
      kept: v.kept ?? 0,
      currentDay: v.currentDay ?? '',
    });
  }

  async termsBase(id: ReservationId): Promise<Result<StayTermsBase[]>> {
    const r = await this.db.rpc('pms_room_stay_terms_base', { p_reservation_id: id });
    return fromList<{ room_stay_id: string; base_cents: number }, StayTermsBase>(
      r as { data: { room_stay_id: string; base_cents: number }[] | null; error: typeof r.error },
      (x) => ({ roomStayId: x.room_stay_id, baseCents: Number(x.base_cents) }),
    );
  }

  async transitionRoomStay(
    id: RoomStayId,
    action: string,
    reason?: string | null,
  ): Promise<Result<Reservation['status']>> {
    return fromResult<Reservation['status']>(
      await this.db.rpc('pms_room_stay_transition', {
        p_room_stay_id: id,
        p_action: action,
        // Omitted-when-absent, for the same reason as `transition` above —
        // except that here the deploy-order argument does NOT apply: this RPC
        // ships in 0091 and has no earlier signature to resolve against. Kept
        // identical anyway, because two sibling calls that differ for reasons
        // one of them no longer has is how the next reader gets misled.
        ...(reason == null || reason === '' ? {} : { p_reason: reason }),
      }),
    );
  }

  async roomStays(id: ReservationId): Promise<Result<RoomStayView[]>> {
    // RELEASED ONES INCLUDED — see the port. `pms_room_stay` carries no
    // tenant_code (a logement references no guest), so property scoping is the
    // whole of it, exactly as the table's RLS policy does.
    const r = await scopedQuery(
      this.db.from('pms_room_stay').select(ROOM_STAY_SELECT).eq('reservation_id', id),
      this.scope.current(),
    )
      .order('checkin_date', { ascending: true })
      .order('created_at', { ascending: true });
    return fromList<RoomStayRow, RoomStayView>(r, rowToRoomStay);
  }

  async listAllRoomStays(): Promise<Result<RoomStayView[]>> {
    // LIVE only — see the port. Filtered in the query rather than by the
    // caller, so a view that forgot cannot draw a released room as occupied.
    const r = await scopedQuery(
      this.db.from('pms_room_stay').select(ROOM_STAY_SELECT, { count: 'exact' }).is('cancelled_at', null),
      this.scope.current(),
    ).order('checkin_date', { ascending: true });
    return fromWholeTable<RoomStayRow, RoomStayView>(r, 'logements', rowToRoomStay);
  }

  async rackWindow(from: IsoDate, to: IsoDate): Promise<Result<RackWindow>> {
    const r = await this.db.rpc('pms_rack_window', {
      p_from: from,
      p_to: to,
      p_property_code: scopedRpcProperty(this.scope.current()),
    });
    if (r.error) return readRpcError(r.error, "Accès réservé — plan d'occupation");
    const v = (r.data ?? null) as { stays?: unknown; reservations?: unknown } | null;
    // A shape we cannot read is an error, not an empty rack: an empty rack
    // says every room is free.
    if (v === null || !Array.isArray(v.stays) || !Array.isArray(v.reservations)) {
      return err('unknown', "Plan d'occupation : réponse illisible", { retryable: false });
    }
    return ok({
      stays: (v.stays as RoomStayRow[]).map(rowToRoomStay),
      reservations: (v.reservations as ReservationRow[]).map(rowToReservationView),
    });
  }

  async searchBookings(query: string, limit = 20): Promise<Result<BookingSearchResult>> {
    const r = await this.db.rpc('pms_reservation_search', {
      p_query: query,
      p_property_code: scopedRpcProperty(this.scope.current()),
      p_limit: limit,
    });
    if (r.error) return readRpcError(r.error, 'Accès réservé — recherche de réservation');
    const found = toBookingSearch((r.data ?? null) as BookingSearchJson | null);
    // A shape we cannot read is an error, not « nothing found ».
    if (!found) return err('unknown', 'Recherche de réservation : réponse illisible', { retryable: false });
    return ok(found);
  }

  async findById(id: ReservationId): Promise<Result<ReservationView | null>> {
    const r = await scopedQuery(
      this.db.from('pms_reservation').select(RESERVATION_SELECT).eq('id', id),
      this.scope.current(),
    ).maybeSingle();
    if (r.error) return fromResult<ReservationView>(r);
    return ok(r.data ? rowToReservationView(r.data as ReservationRow) : null);
  }

  async listAll(): Promise<Result<ReservationView[]>> {
    const r = await scopedQuery(
      this.db.from('pms_reservation').select(RESERVATION_SELECT, { count: 'exact' }),
      this.scope.current(),
    ).order('checkin_date', { ascending: true });
    return fromWholeTable<ReservationRow, ReservationView>(r, 'réservations', rowToReservationView);
  }

  async listArriving(from: IsoDate, to: IsoDate): Promise<Result<ReservationView[]>> {
    const r = await scopedQuery(
      this.db
        .from('pms_reservation')
        .select(RESERVATION_SELECT, { count: 'exact' })
        .gte('checkin_date', from)
        .lte('checkin_date', to),
      this.scope.current(),
    ).order('checkin_date', { ascending: true });
    return fromWholeTable<ReservationRow, ReservationView>(r, 'réservations', rowToReservationView);
  }

  async listOverlapping(from: IsoDate, to: IsoDate): Promise<Result<ReservationView[]>> {
    const r = await scopedQuery(
      this.db
        .from('pms_reservation')
        .select(RESERVATION_SELECT)
        .lte('checkin_date', to)
        .gte('checkout_date', from),
      this.scope.current(),
    ).order('checkin_date', { ascending: true });
    return fromList<ReservationRow, ReservationView>(r, rowToReservationView);
  }

  async applyInboundBooking(booking: InboundBooking): Promise<Result<InboundApplied | null>> {
    // The RPC takes the établissement from the PAYLOAD (it runs as the service
    // role, which holds no grant and so cannot infer one). This repository is
    // bound to a property. If those two ever disagree, the booking lands
    // somewhere nobody asked for — and neither RLS nor a foreign key would say
    // so, because both destinations are perfectly valid rows.
    //
    // Cheap to check, and the only place the two facts meet.
    const scope = this.scope.current();
    if (scope.kind === 'property' && booking.propertyCode !== scope.propertyCode) {
      return err(
        'validation',
        `Réservation entrante pour « ${booking.propertyCode} » reçue par l'adaptateur de « ${scope.propertyCode} »`,
        { retryable: false },
      );
    }
    const r = await this.db.rpc('pms_apply_inbound_booking', {
      p_booking: booking as unknown as Record<string, unknown>,
    });
    if (r.error) return fromResult<InboundApplied | null>(r);
    // Since 0093 the RPC returns { reservationId, warnings[] } — a uuid had
    // nowhere to carry « applied, and worth a look ».
    const row = (r.data ?? null) as { reservationId?: string | null; warnings?: unknown } | null;
    // Null reservationId = cancel for a booking we never saw (§8) — a
    // legitimate no-op, and NOT an error.
    if (row === null || row.reservationId == null) return ok(null);
    return ok({
      reservationId: row.reservationId as ReservationId,
      warnings: Array.isArray(row.warnings) ? (row.warnings as InboundApplied['warnings']) : [],
    });
  }

  async findByChannexBookingId(channexBookingId: string): Promise<Result<Reservation | null>> {
    // channex_booking_id is unique PER PROPERTY since step 3, so an unscoped
    // match could return another etablissement's booking to the webhook.
    const r = await scopedQuery(
      this.db
        .from('pms_reservation')
        .select(RESERVATION_SELECT)
        .eq('channex_booking_id', channexBookingId),
      this.scope.current(),
    ).maybeSingle();
    if (r.error) return fromResult<Reservation>(r);
    return ok(r.data ? rowToReservationView(r.data as ReservationRow) : null);
  }

  async events(id: ReservationId): Promise<Result<ReservationEvent[]>> {
    const r = await this.db
      .from('pms_reservation_event')
      // operator_label: « Marie D. », computed by the database (ADR-0026, O5).
      .select('*, operator_label')
      .eq('reservation_id', id)
      .order('created_at', { ascending: true });
    return fromList<ReservationEventRow, ReservationEvent>(r, rowToReservationEvent);
  }

  async chargeDays(id: ReservationId): Promise<Result<ChargeDay[]>> {
    // ACTIVE lines only. Voided ones are history and belong to the audit read —
    // a form that loaded them would send them straight back as if they were
    // still owed, which is the one way void-and-append can be undone by a
    // client that means well.
    const r = await this.db
      .from('pms_charge_day')
      // The charge's origin rides along: the form must not reload a line the
      // persons derive (group edit step 4) as a request of its own.
      .select('*, pms_charge!pms_charge_day_charge_same_property(origin)')
      .eq('reservation_id', id)
      .is('voided_at', null)
      .order('date', { ascending: true })
      .order('kind', { ascending: true });
    return fromList<ChargeDayRow, ChargeDay>(r, rowToChargeDay);
  }

  // ── The folio's read path (0096-0099) ─────────────────────────────────────
  // Plain reads, composed into a document by the domain's pure functions. No
  // RPC: `pms_charge` already carries the range and the quantity, so every
  // total — « owed as of D » included — is arithmetic over these rows.

  async charges(id: ReservationId): Promise<Result<Charge[]>> {
    // ACTIVE only, for the reason the day read gives: a voided agreement is
    // history, and a grid that loaded it would send it back as though it still
    // stood.
    const r = await this.db
      .from('pms_charge')
      .select('*')
      .eq('reservation_id', id)
      .is('voided_at', null)
      .order('start_date', { ascending: true })
      .order('kind', { ascending: true });
    return fromList<ChargeRow, Charge>(r, rowToCharge);
  }

  async folios(id: ReservationId): Promise<Result<Folio[]>> {
    const r = await this.db
      .from('pms_folio')
      .select('*')
      .eq('reservation_id', id)
      .order('id', { ascending: true });
    return fromList<FolioRow, Folio>(r, rowToFolio);
  }

  async occupancy(id: RoomStayId): Promise<Result<OccupancySpan[]>> {
    const r = await this.db
      .from('pms_stay_occupancy')
      .select('*')
      .eq('room_stay_id', id)
      .order('start_date', { ascending: true });
    return fromList<OccupancyRow, OccupancySpan>(r, rowToOccupancySpan);
  }

  async persons(id: ReservationId): Promise<Result<StayPerson[]>> {
    // A 25-room group is ~50 rows; the cap is checked anyway, because a list
    // that silently ends is a person who silently vanishes at the next save.
    const r = await scopedQuery(
      this.db.from('pms_stay_person').select('*', { count: 'exact' }).eq('reservation_id', id),
      this.scope.current(),
    )
      .order('room_stay_id', { ascending: true })
      .order('position', { ascending: true });
    return fromWholeTable<StayPersonRow, StayPerson>(r, 'personnes', rowToStayPerson);
  }

  async personMeals(id: ReservationId): Promise<Result<StayPersonMeal[]>> {
    const r = await scopedQuery(
      this.db.from('pms_stay_person_meal').select('person_id, service_date, prestation, taken', { count: 'exact' }).eq('reservation_id', id),
      this.scope.current(),
    ).order('service_date', { ascending: true });
    return fromWholeTable<StayPersonMealRow, StayPersonMeal>(r, 'exceptions de repas', rowToStayPersonMeal);
  }

  async personNeeds(id: ReservationId): Promise<Result<StayPersonNeeds[]>> {
    // Health data: the columns named, never `*`, so nothing added to the table
    // later reaches a screen by accident.
    const r = await scopedQuery(
      this.db.from('pms_stay_person_needs').select('person_id, needs, allergens', { count: 'exact' }).eq('reservation_id', id),
      this.scope.current(),
    ).order('person_id', { ascending: true });
    return fromWholeTable<StayPersonNeedsRow, StayPersonNeeds>(r, 'besoins et allergies', rowToStayPersonNeeds);
  }

  async otaServices(id: ReservationId): Promise<Result<OtaService[]>> {
    // pms_ota_service hangs off the LOGEMENT, not the booking — it is what a
    // channel sold with a particular room — so the booking's rooms are resolved
    // first. An inner join on the embedded resource rather than two round
    // trips, and `room_stay_id` is what the folio groups by afterwards.
    const r = await this.db
      .from('pms_ota_service')
      .select('*, pms_room_stay!inner(reservation_id)')
      .eq('pms_room_stay.reservation_id', id)
      .order('id', { ascending: true });
    return fromList<OtaServiceRow, OtaService>(r, rowToOtaService);
  }

  async writeOccupancy(
    id: RoomStayId,
    spans: readonly OccupancySpanInput[],
  ): Promise<Result<Cents>> {
    return fromResult<number>(
      await this.db.rpc('pms_write_occupancy', {
        p_room_stay_id: id,
        p_spans: spans.map((s) => ({
          startDate: s.startDate,
          endDate: s.endDate,
          adults: s.adults,
          children: s.children,
          infants: s.infants,
        })),
      }),
    );
  }

  async setBillingMode(id: ReservationId, mode: BillingMode): Promise<Result<number>> {
    return fromResult<number>(
      await this.db.rpc('pms_set_billing_mode', { p_reservation_id: id, p_mode: mode }),
    );
  }

  async moveCharges(
    chargeIds: readonly ChargeId[],
    folioId: FolioId,
  ): Promise<Result<number>> {
    return fromResult<number>(
      await this.db.rpc('pms_move_charges', {
        p_charge_ids: [...chargeIds],
        p_folio_id: folioId,
      }),
    );
  }

  async markVadDone(id: ReservationId): Promise<Result<void>> {
    return fromVoid(await this.db.rpc('pms_reservation_mark_vad_done', { p_id: id }));
  }

  async markTransferReceived(id: ReservationId): Promise<Result<void>> {
    return fromVoid(await this.db.rpc('pms_reservation_mark_transfer_received', { p_id: id }));
  }
}

/** camelCase patch → the RPC's snake_case keys. Only the keys PRESENT travel:
 *  absent means unchanged, and `pms_save_guest_sheet` refuses a key it does
 *  not know rather than dropping it. */
const GUEST_PATCH_COLUMNS: Record<keyof GuestSheetPatch, string> = {
  lastName: 'last_name',
  firstName: 'first_name',
  email: 'email',
  phone: 'phone',
  mobile: 'mobile',
  addressLine1: 'address_line1',
  addressLine2: 'address_line2',
  postalCode: 'postal_code',
  city: 'city',
  countryCode: 'country_code',
  lang: 'lang',
  notes: 'notes',
  customerType: 'customer_type',
  customerSubtype: 'customer_subtype',
  siren: 'siren',
  vatNumber: 'vat_number',
};

export function guestPatchFields(patch: GuestSheetPatch): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(patch)) {
    const column = GUEST_PATCH_COLUMNS[k as keyof GuestSheetPatch];
    if (column && v !== undefined) out[column] = v;
  }
  return out;
}

/** The client RPCs raise in French behind their own name
 *  (« pms_save_guest_sheet: … »); the desk reads the sentence, not the name.
 *  A missing capability (42501) is a denial, never « no such client ». */
function guestRpcError(e: PostgrestError): Result<never> {
  return readRpcError(e, 'Accès réservé — fiche client');
}

/** A raising read RPC's error, for the desk: a denial (42501) is `forbidden`
 *  with the sentence given, and a refusal the RPC worded (P0001) keeps its
 *  words without the function's name in front. */
function readRpcError(e: PostgrestError, denied: string): Result<never> {
  if (e.code === '42501') return err('forbidden', denied, { cause: e });
  if (e.code === 'P0001') return err('validation', e.message.replace(/^pms_[a-z_]+:\s*/, ''), { cause: e });
  return { ok: false, error: toPmsError(e) };
}

/** A whole-table read that REFUSES past PostgREST's row cap instead of
 *  returning its first page as if it were everything. `max_rows` (config.toml)
 *  cuts a select at 1 000 rows with no error (memory: postgrest-max-rows-cap);
 *  `count: 'exact'` is what makes the cut visible — the response carries the
 *  true total beside the rows it kept. A list that silently ends reads as « no
 *  more bookings »; one that says it could not load them is only annoying. */
function fromWholeTable<Row, T>(
  r: { data: Row[] | null; error: PostgrestError | null; count: number | null },
  what: string,
  map: (row: Row) => T,
): Result<T[]> {
  if (!r.error && r.data !== null && r.count !== null && r.count > r.data.length) {
    return err(
      'unknown',
      `Liste incomplète refusée : ${r.count} ${what} à lire, ${r.data.length} reçus d'un coup`,
      { retryable: false },
    );
  }
  return fromList<Row, T>(r, map);
}

export class SupabaseGuestRepository implements GuestRepository {
  constructor(
    private readonly db: SupabaseClient,
    private readonly scope: ScopeSource,
  ) {}

  async upsert(id: GuestId | null, draft: GuestDraft): Promise<Result<GuestId>> {
    return fromResult<GuestId>(
      await this.db.rpc('pms_upsert_guest', {
        p_property_code: scopedRpcProperty(this.scope.current()),
        p_id: id,
        p_last_name: draft.lastName,
        p_first_name: draft.firstName,
        p_email: draft.email,
        p_phone: draft.phone,
        p_lang: draft.lang,
        p_notes: draft.notes,
      }),
    );
  }

  async findById(id: GuestId): Promise<Result<Guest | null>> {
    const r = await this.db.from('pms_guest').select('*').eq('id', id).maybeSingle();
    if (r.error) return fromResult<Guest>(r);
    return ok(r.data ? rowToGuest(r.data as GuestRow) : null);
  }

  async search(q: GuestSearch): Promise<Result<GuestSearchResult>> {
    const limit = Math.min(Math.max(q.limit ?? 30, 1), 50);
    // One more than asked: the extra row is how « there were more » is known
    // without counting the table.
    const r = await this.db.rpc('pms_guest_search', {
      p_query: q.query,
      p_customer_type: q.customerType ?? null,
      p_missing_siren: q.missingSiren ?? false,
      p_consent_required: q.consentRequired ?? false,
      p_limit: limit + 1,
      p_property_code: scopedRpcProperty(this.scope.current()),
    });
    if (r.error) return guestRpcError(r.error);
    const rows = (r.data ?? []) as GuestSearchRow[];
    return ok({ hits: rows.slice(0, limit).map(rowToGuestSummary), truncated: rows.length > limit });
  }

  async sheet(id: GuestId): Promise<Result<GuestSheet>> {
    const r = await this.db.rpc('pms_guest_sheet', { p_id: id });
    if (r.error) return guestRpcError(r.error);
    const row = ((r.data ?? []) as GuestSheetRow[])[0];
    return row ? ok(rowToGuestSheet(row)) : err('not_found', 'Client introuvable');
  }

  async saveSheet(id: GuestId, patch: GuestSheetPatch): Promise<Result<void>> {
    const r = await this.db.rpc('pms_save_guest_sheet', { p_id: id, p_fields: guestPatchFields(patch) });
    return r.error ? guestRpcError(r.error) : ok(undefined);
  }

  async listSubtypes(): Promise<Result<CustomerSubtype[]>> {
    return fromList<CustomerSubtypeRow, CustomerSubtype>(
      await this.db.from('pms_customer_subtype').select('*').order('sort_order'),
      rowToCustomerSubtype,
    );
  }

  async findDuplicates(email: string | null, phone: string | null): Promise<Result<Guest[]>> {
    if (!email && !phone) return ok([]);
    const ors = [
      email ? `email.eq.${email}` : null,
      phone ? `phone.eq.${phone}` : null,
    ]
      .filter(Boolean)
      .join(',');
    return fromList<GuestRow, Guest>(
      await this.db.from('pms_guest').select('*').or(ors),
      rowToGuest,
    );
  }
}

export class SupabaseRoomRepository implements RoomRepository {
  constructor(
    private readonly db: SupabaseClient,
    private readonly scope: ScopeSource,
  ) {}

  /** Rooms, each carrying the arrangements it can be made up in.
   *
   *  ⚠ TWO round trips, deliberately. The options could ride along as an
   *  embedded PostgREST resource, but the room list is read by the rack, the
   *  fiche and the Configuration table, and an embed changes the row shape for
   *  all three. Two plain reads joined in memory keep `RoomRow` exactly what the
   *  table is — and at 42 rooms the cost is not a consideration.
   *
   *  A room with NO options is not an error: that is a multi-space unit, whose
   *  beds live in `pms_room_bed` (the XOR, `pmsIsMultiSpace`). */
  async listRooms(): Promise<Result<Room[]>> {
    const rooms = await scopedQuery(
      this.db.from('pms_room').select('*'),
      this.scope.current(),
    ).order('room_number');
    if (rooms.error) return fromList<RoomRow, Room>(rooms, rowToRoom);

    // Same error mapping as every other read — hand-rolling one here is how a
    // failure starts surfacing differently from its neighbours.
    const opts = await fromList<RoomConfigOptionRow, RoomConfigOptionRow>(
      await scopedQuery(
        this.db.from('pms_room_config_option').select('room_id,bed_config'),
        this.scope.current(),
      ).order('bed_config'),
      (r) => r,
    );
    if (!opts.ok) return opts;

    const byRoom = new Map<number, string[]>();
    for (const o of opts.value) {
      const list = byRoom.get(o.room_id);
      if (list) list.push(o.bed_config);
      else byRoom.set(o.room_id, [o.bed_config]);
    }

    return ok(((rooms.data ?? []) as RoomRow[]).map((r) => rowToRoom(r, byRoom.get(r.id) ?? [])));
  }

  async listRoomTypes(): Promise<Result<RoomType[]>> {
    return fromList<RoomTypeRow, RoomType>(
      await scopedQuery(this.db.from('pms_room_type').select('*'), this.scope.current()).order('code'),
      rowToRoomType,
    );
  }

  async setChannexRoomTypeId(id: RoomTypeId, channexId: string | null): Promise<Result<void>> {
    // Direct PostgREST write, the shape upsertRates and the room blocks already
    // use (D4): pms_room_type carries an ALL policy under `pms.room.write`, so
    // the invariant is the policy's and there is nothing for an RPC to own.
    return fromVoid(
      await scopedQuery(
        this.db.from('pms_room_type').update({ channex_room_type_id: channexId }).eq('id', id),
        this.scope.current(),
      ),
    );
  }

  /** `pms_bed_type` (0173) — the FURNITURE vocabulary. Separate from
   *  `listBedConfigs`, which is the ARRANGEMENT vocabulary; keeping one call
   *  for both would re-merge the two concepts 0173 split. */
  async listBedTypes(): Promise<Result<BedType[]>> {
    return fromList<BedTypeRow, BedType>(
      await scopedQuery(this.db.from('pms_bed_type').select('*').eq('active', true), this.scope.current()).order(
        'sort_order',
      ),
      rowToBedType,
    );
  }

  async setRoomCurrentConfig(
    roomId: RoomId,
    bedConfig: string | null,
    source: 'menage' | 'desk',
  ): Promise<Result<void>> {
    return fromVoid(
      await this.db.rpc('pms_set_room_current_config', {
        p_room_id: roomId,
        p_bed_config: bedConfig,
        p_source: source,
      }),
    );
  }

  async listBedConfigs(): Promise<Result<BedConfig[]>> {
    return fromList<BedConfigRow, BedConfig>(
      await scopedQuery(this.db.from('pms_bed_config').select('*').eq('active', true), this.scope.current()).order(
        'sort_order',
      ),
      rowToBedConfig,
    );
  }

  /** Every space's beds for the établissement in scope. Read separately rather
   *  than embedded, so the Configuration table keeps the shape it already has:
   *  rooms, types and bed configs are three independent lists, and this is a
   *  fourth. Grouped by room by the caller. */
  async listRoomBeds(): Promise<Result<RoomBed[]>> {
    return fromList<RoomBedRow, RoomBed>(
      await scopedQuery(this.db.from('pms_room_bed').select('*'), this.scope.current())
        .order('space_kind')
        .order('space_index'),
      rowToRoomBed,
    );
  }

  async createRoom(draft: NewRoom): Promise<Result<RoomId>> {
    return fromResult<RoomId>(
      await this.db.rpc('pms_create_room', {
        p_property_code: scopedRpcProperty(this.scope.current()),
        p_room_number: draft.roomNumber,
        p_floor: draft.floor,
        p_room_type_id: draft.roomTypeId,
        p_config_options: draft.configOptions,
        p_double_bed_type: draft.doubleBedType,
        p_max_occupancy: draft.maxOccupancy,
        // Omitted (undefined -> null) the database resolves unit_kind from the
        // établissement's own kind, so the create form never has to know it is
        // making an apartment.
        p_unit: draft.unit ?? null,
        p_beds: bedsPayload(draft.beds),
        p_new_type: draft.newType ?? null,
      }),
    );
  }

  async deleteRoom(roomId: RoomId): Promise<Result<void>> {
    return fromVoid(await this.db.rpc('pms_delete_room', { p_room_id: roomId }));
  }

  async updateConfig(roomId: RoomId, patch: RoomConfigPatch): Promise<Result<void>> {
    return fromVoid(
      await this.db.rpc('pms_update_room_config', {
        p_room_id: roomId,
        p_room_type_id: patch.roomTypeId,
        p_config_options: patch.configOptions,
        p_double_bed_type: patch.doubleBedType,
        p_max_occupancy: patch.maxOccupancy,
        p_is_superior: patch.isSuperior,
        p_heating_zone: patch.heatingZone,
        p_bathroom_heating_zone: patch.bathroomHeatingZone,
        p_room_view: patch.view,
        p_has_shower: patch.hasShower,
        p_wc_not_separated: patch.wcNotSeparated,
        p_remarks: patch.remarks,
        // null means "not managing this", NOT "clear it" — the distinction the
        // RPC's jsonb parameters exist to preserve (P1.6 step 3).
        p_unit: patch.unit,
        p_beds: bedsPayload(patch.beds),
        p_type: patch.type,
      }),
    );
  }

  // (P2) targets pms_set_hk_status (§8 draft) — unused by the P1 UI.
  async setHkStatus(roomId: RoomId, status: Room['hkStatus']): Promise<Result<void>> {
    return fromVoid(
      await this.db.rpc('pms_set_hk_status', { p_room_id: roomId, p_status: status }),
    );
  }

  async listBlocks(from: IsoDate, to: IsoDate): Promise<Result<RoomBlock[]>> {
    return fromList<RoomBlockRow, RoomBlock>(
      await this.db
        .from('pms_room_block')
        .select('*')
        .lt('start_date', to)
        .gt('end_date', from),
      rowToRoomBlock,
    );
  }

  async addBlock(block: Omit<RoomBlock, 'id'>): Promise<Result<number>> {
    const r = await this.db
      .from('pms_room_block')
      .insert({
        // Explicit since step 7 — the UI now carries a scope, so the column
        // default that stood in for it through steps 4-6 is gone.
        ...scopedInsert(this.scope.current()),
        room_id: block.roomId,
        start_date: block.startDate,
        end_date: block.endDate,
        reason: block.reason,
      })
      .select('id')
      .single();
    if (r.error) return fromResult<number>(r);
    return ok((r.data as { id: number }).id);
  }

  async removeBlock(blockId: number): Promise<Result<void>> {
    return fromVoid(await this.db.from('pms_room_block').delete().eq('id', blockId));
  }
}

export class SupabaseRateRepository implements RateRepository {
  constructor(
    private readonly db: SupabaseClient,
    private readonly scope: ScopeSource,
  ) {}

  async listPlans() {
    return fromList<RatePlanRow, ReturnType<typeof rowToRatePlan>>(
      await scopedQuery(this.db.from('pms_rate_plan').select('*'), this.scope.current()).order('code'),
      rowToRatePlan,
    );
  }

  async listChannexRatePlans(): Promise<Result<ChannexRatePlanLink[]>> {
    return fromList<ChannexRatePlanRow, ChannexRatePlanLink>(
      await scopedQuery(
        this.db.from('pms_channex_rate_plan').select('*'),
        this.scope.current(),
      ).order('rate_plan_id'),
      rowToChannexRatePlanLink,
    );
  }

  // ── Rate parameter sets (0107) ─────────────────────────────────────────

  async listParameterSets(): Promise<Result<StoredRateParameterSet[]>> {
    return fromList<RateParameterSetRow, StoredRateParameterSet>(
      await scopedQuery(
        this.db
          .from('pms_rate_parameter_set')
          .select('id,name,note,status,parameters,published_at'),
        this.scope.current(),
      ).order('id', { ascending: false }),
      rowToRateParameterSet,
    );
  }

  async getParameterSet(id: number): Promise<Result<StoredRateParameterSet | null>> {
    const r = await scopedQuery(
      this.db
        .from('pms_rate_parameter_set')
        .select('id,name,note,status,parameters,published_at')
        .eq('id', id),
      this.scope.current(),
    ).maybeSingle();
    if (r.error) return fromResult<StoredRateParameterSet | null>(r);
    // `null` is a legitimate answer, not an error: an id that does not exist
    // and an id the actor may not see are the same thing under RLS, and the
    // caller must handle "not found" either way.
    return ok(r.data === null ? null : rowToRateParameterSet(r.data as RateParameterSetRow));
  }

  async createParameterSetDraft(draft: RateParameterSet, note: string | null): Promise<Result<number>> {
    const r = await this.db
      .from('pms_rate_parameter_set')
      .insert({
        ...scopedInsert(this.scope.current()),
        name: draft.name,
        note,
        // Never 'published' on insert: publishing is what freezes a set, and a
        // set born frozen would make a typo permanent in one call (the port
        // says the same, and this is the half that is enforced).
        status: 'draft',
        parameters: draft,
      })
      .select('id')
      .single();
    if (r.error) return fromResult<number>(r);
    return ok((r.data as { id: number }).id);
  }

  async updateParameterSetDraft(
    id: number,
    draft: RateParameterSet,
    note: string | null,
  ): Promise<Result<void>> {
    // No status filter here on purpose. The database refuses this for a
    // published set (0107's guard trigger), and adding `.eq('status','draft')`
    // would turn a loud refusal into a silent no-op — the failure mode this
    // whole table exists to prevent.
    return fromVoid(
      await scopedQuery(
        this.db
          .from('pms_rate_parameter_set')
          .update({ name: draft.name, note, parameters: draft })
          .eq('id', id),
        this.scope.current(),
      ),
    );
  }

  async publishParameterSet(id: number): Promise<Result<void>> {
    // `published_at` is stamped by the trigger, not sent from here: the pair
    // (status, published_at) is what a provenance lookup reads, and a client
    // clock has no business setting it.
    return fromVoid(
      await scopedQuery(
        this.db.from('pms_rate_parameter_set').update({ status: 'published' }).eq('id', id),
        this.scope.current(),
      ),
    );
  }

  async archiveParameterSet(id: number): Promise<Result<void>> {
    return fromVoid(
      await scopedQuery(
        this.db.from('pms_rate_parameter_set').update({ status: 'archived' }).eq('id', id),
        this.scope.current(),
      ),
    );
  }

  async deleteParameterSetDraft(id: number): Promise<Result<void>> {
    return fromVoid(
      await scopedQuery(
        this.db.from('pms_rate_parameter_set').delete().eq('id', id),
        this.scope.current(),
      ),
    );
  }

  async setChannexRatePlan(
    ratePlanId: RatePlanId,
    roomTypeId: RoomTypeId,
    channexId: string | null,
    occupancyOptions: ChannexOccupancyOption[] = [],
  ): Promise<Result<void>> {
    // Clearing is a DELETE, not a null: the row's whole content is the binding,
    // so an unbound pair is an absent row rather than a row saying nothing.
    if (channexId === null) {
      return fromVoid(
        await scopedQuery(
          this.db.from('pms_channex_rate_plan').delete().eq('rate_plan_id', ratePlanId),
          this.scope.current(),
        ).eq('room_type_id', roomTypeId),
      );
    }
    const scoped = scopedInsert(this.scope.current());
    return fromVoid(
      await this.db.from('pms_channex_rate_plan').upsert(
        {
          ...scoped,
          rate_plan_id: ratePlanId,
          room_type_id: roomTypeId,
          channex_rate_plan_id: channexId,
          // ONE upsert carries both (0104), so a binding never exists without
          // the occupancies it must be pushed to — Hard Rule 2 applied to a
          // mapping that two writes could leave half-formed.
          channex_options: Object.fromEntries(
            occupancyOptions.map((o) => [String(o.occupancy), o.channexOptionId]),
          ),
        },
        { onConflict: 'rate_plan_id,room_type_id' },
      ),
    );
  }

  async listProducts(): Promise<Result<Product[]>> {
    return fromList<ProductRow, Product>(
      await scopedQuery(
        // The stay's extras and the CA categories its charges name — never the till's carte (T2), which has
        // no recurrence over a stay and is edited under its own right.
        this.db.from('pms_product').select('*').eq('active', true).or('role.is.null,is_category.eq.true'),
        this.scope.current(),
      )
        .order('sort_order')
        .order('label_fr'),
      rowToProduct,
    );
  }

  // (P2) the ARI grid read/write — tables exist at P1, unused by the P1 UI.
  async listRates(
    ratePlanIds: readonly number[],
    roomTypeId: number,
    from: IsoDate,
    to: IsoDate,
  ): Promise<Result<Rate[]>> {
    // `.in(...)`, not a call per plan: one room type's slice across every plan
    // is ONE query. See the port — the loop this replaces is what put a
    // six-job ari_push tick over the runtime's subrequest ceiling.
    if (ratePlanIds.length === 0) return ok([]);
    const r = await this.db
      .from('pms_rate')
      .select('*')
      .in('rate_plan_id', [...ratePlanIds])
      .eq('room_type_id', roomTypeId)
      .gte('date', from)
      .lt('date', to);
    if (r.error) return fromResult<Rate[]>(r);
    return ok(
      (r.data ?? []).map((x: Record<string, unknown>) => ({
        ratePlanId: x.rate_plan_id as number,
        roomTypeId: x.room_type_id as number,
        date: x.date as string,
        priceCents: x.price_cents as number,
        minStay: x.min_stay as number,
        closed: x.closed as boolean,
        ...rateRestrictionFields(x as Pick<RateRow, 'closed_to_arrival' | 'closed_to_departure' | 'max_stay'>),
      })),
    );
  }

  async upsertProduct(draft: ProductDraft): Promise<Result<ProductId>> {
    // Direct, RLS-audited (D4), like `upsertRates` below. Every rule the
    // catalogue has is a CHECK on the row — a tax bears no TVA, a sale must
    // state one, a tax is per person per night — so the database refuses a bad
    // product whatever writes it, and an RPC would only re-state them.
    const row = {
      ...scopedInsert(this.scope.current()),
      code: draft.code,
      label_fr: draft.labelFr,
      description_fr: draft.descriptionFr,
      kind: draft.kind,
      emoji: draft.emoji,
      applies_to: draft.appliesTo,
      per_person: draft.perPerson,
      price_cents: draft.priceCents,
      tva_rate: draft.tvaRate,
      is_tax: draft.isTax,
      auto_apply: draft.autoApply,
      manual_qty: draft.manualQty,
      sort_order: draft.sortOrder,
      updated_at: new Date().toISOString(),
    };
    // `active` is absent on purpose: retiring is `setProductActive`, so an edit
    // cannot silently restore something the operator withdrew from sale.
    const q =
      draft.id === undefined
        ? this.db.from('pms_product').insert(row).select('id').single()
        : this.db.from('pms_product').update(row).eq('id', draft.id).select('id').single();
    const r = await q;
    if (r.error) return fromResult<ProductId>(r);
    return ok((r.data as { id: number }).id);
  }

  async setProductActive(id: ProductId, active: boolean): Promise<Result<void>> {
    // NEVER a delete. pms_charge and pms_ota_service both name a product, so a
    // billed one cannot be removed — and a past folio must keep reading
    // « Demi-pension » long after it stops being sold.
    return fromVoid(
      await this.db
        .from('pms_product')
        .update({ active, updated_at: new Date().toISOString() })
        .eq('id', id),
    );
  }

  async listRateGrid(from: IsoDate, to: IsoDate): Promise<Result<Rate[]>> {
    // Property-scoped, unlike `listRates` above — that one is keyed by a
    // (plan, type) pair whose ids are already scoped, while this reads across
    // the whole catalogue and would otherwise cross établissements.
    // `lte` on the end date, not `lt`: the grid's range is inclusive because
    // the operator picks « du 1er au 14 » and means the 14th.
    const r = await scopedQuery(
      this.db.from('pms_rate').select('*').gte('date', from).lte('date', to),
      this.scope.current(),
    )
      .order('date')
      .order('rate_plan_id')
      .order('room_type_id');
    if (r.error) return fromResult<Rate[]>(r);
    return ok((r.data ?? []).map(rowToRate));
  }

  /**
   * THE HAND PATH — every row written here is marked `hand_adjusted` (0117).
   *
   * ⚠ Do NOT reuse this for the grid generator. It is the « a human typed
   * this » write, and the marker is what stops a later regeneration destroying
   * the operator's own prices (R4). A generator that came through here would
   * mark its own output as hand-typed and then, on the next run, politely
   * refuse to update anything it had ever written — the guard inverted into a
   * freeze. The generator gets its own path, writing FALSE
   * (PMS-SEASON-CALENDAR-BRIEF §5.1).
   */
  async upsertRates(rates: Rate[]): Promise<Result<void>> {
    return fromVoid(
      await this.db.from('pms_rate').upsert(
        rates.map((x) => ({
          ...scopedInsert(this.scope.current()),
          rate_plan_id: x.ratePlanId,
          room_type_id: x.roomTypeId,
          date: x.date,
          price_cents: x.priceCents,
          min_stay: x.minStay,
          closed: x.closed,
          ...rateRestrictionColumns(x),
          hand_adjusted: true,
        })),
      ),
    );
  }

  /**
   * THE GENERATED PATH — through `pms_generate_rates` (0119), never an upsert.
   *
   * The guard is a conditional `ON CONFLICT … WHERE NOT hand_adjusted` that
   * PostgREST cannot express, so doing this client-side would mean a read, a
   * filter and a write with a race in the middle — and a guard the next code
   * path can forget. The RPC also returns what it DECLINED, which the caller
   * must show: a generator that silently skipped 34 days would leave the
   * operator believing the grid was refreshed.
   */
  async generateRates(rates: Rate[]): Promise<Result<{ written: number; skipped: number }>> {
    if (rates.length === 0) return ok({ written: 0, skipped: 0 });
    const r = await this.db.rpc('pms_generate_rates', {
      p_property_code: scopedRpcProperty(this.scope.current()),
      p_rows: rates.map((x) => ({
        ratePlanId: x.ratePlanId,
        roomTypeId: x.roomTypeId,
        date: x.date,
        priceCents: x.priceCents,
        minStay: x.minStay,
        closed: x.closed,
      })),
    });
    if (r.error) return fromResult<{ written: number; skipped: number }>(r as never);
    // The RPC has OUT parameters, so PostgREST answers with a single-row
    // record — or an array of one, depending on how it is called. Both are
    // handled rather than assumed, because guessing wrong here reports
    // « 0 générés » for a run that wrote two thousand rows.
    const row = (Array.isArray(r.data) ? r.data[0] : r.data) as { written: number; skipped: number } | null;
    return ok({ written: row?.written ?? 0, skipped: row?.skipped ?? 0 });
  }
}

export class SupabaseAvailabilityQuery implements AvailabilityQuery {
  constructor(
    private readonly db: SupabaseClient,
    private readonly scope: ScopeSource,
  ) {}

  async range(
    from: IsoDate,
    to: IsoDate,
    roomTypeId?: RoomTypeId,
  ): Promise<Result<AvailabilityCell[]>> {
    const q = this.db.rpc('pms_availability_range', {
      p_from: from,
      p_to: to,
      p_property_code: scopedRpcProperty(this.scope.current()),
    });
    // FILTERED IN POSTGREST, not here. `pms_availability_range` RETURNS TABLE,
    // and PostgREST wraps a set-returning function so its result can be filtered
    // like any relation — the WHERE runs in Postgres and the discarded rows
    // never cross the wire or reach JSON.parse.
    //
    // Deliberately NOT a fourth argument to the RPC. Adding one means dropping
    // and recreating a function that four migrations have already shaped and
    // that the board, the picker and the reconcile all read; a signature change
    // there to save the push some parsing is a poor trade. This gets the same
    // saving with no schema change at all (Hard Rule 4).
    return fromList<AvailabilityRow, AvailabilityCell>(
      await (roomTypeId === undefined ? q : q.eq('room_type_id', roomTypeId)),
      rowToAvailabilityCell,
    );
  }

  // (P2) targets pms_free_rooms (§8 draft) — unused by the P1 UI.
  async freeRooms(
    roomTypeId: RoomTypeId,
    checkin: IsoDate,
    checkout: IsoDate,
  ): Promise<Result<RoomId[]>> {
    const r = await this.db.rpc('pms_free_rooms', {
      p_property_code: scopedRpcProperty(this.scope.current()),
      p_room_type_id: roomTypeId,
      p_checkin: checkin,
      p_checkout: checkout,
    });
    if (r.error) return fromResult<RoomId[]>(r);
    return ok((r.data ?? []) as RoomId[]);
  }
}


// ── Properties — what the operator may switch between (P1.5 step 7) ──────────

export class SupabasePropertyRepository implements PropertyRepository {
  constructor(private readonly db: SupabaseClient) {}

  async listForActor(): Promise<Result<Property[]>> {
    // NO scope filter, and that is the point: this read IS how the scope gets
    // decided, so filtering it by the scope would be circular. RLS on
    // app_property already narrows it to `code = any(actor_properties())`, so
    // the list is exactly the actor's reach — no more, and nothing to add.
    const r = await this.db
      .from('app_property')
      .select('code, tenant_code, label_fr, kind, mode')
      .eq('active', true)
      // Presentation order, and the port says callers must not re-sort. Ordering
      // by `code` alone was unobservable while the hotel was the only row, and
      // stops being so the moment a second exists: 'APPART' < 'HOTEL', so the
      // alphabet would default a multi-property actor into the 3-unit property
      // as their view of a 42-room hotel (P1.6 build brief §3.3).
      .order('sort_order')
      .order('code');
    return fromList<
      { code: string; tenant_code: string; label_fr: string; kind: 'hotel' | 'apartments'; mode: PmsPropertyMode },
      Property
    >(r, (row) => ({
      code: row.code,
      tenantCode: row.tenant_code,
      labelFr: row.label_fr,
      kind: row.kind,
      mode: row.mode,
    }));
  }
}
